import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL } from "@/lib/assets";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  {
    label: "Probate",
    to: "/probates",
    children: [
      { label: "Executor & Administrator Duties", to: "/probates" },
      { label: "When Is Probate Needed?", to: "/when-probate-needed" },
      { label: "Steps to Sell an Inherited Home", to: "/steps-to-sell-inherited-home" },
    ],
  },
  { label: "Referral Services", to: "/referral-services" },
  { label: "Market Your Home", to: "/market-your-home" },
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/contact" },
];

const MORE_LINKS = [
  { label: "Probate FAQs", to: "/probate-faqs" },
  { label: "Process", to: "/#process" },
  { label: "Testimonials", to: "/#testimonials" },
  { label: "Mortgage Calculator", to: "/#mortgage" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const isActive = (to) => {
    if (to === "/") return pathname === "/";
    return pathname.startsWith(to.split("#")[0]) && to !== "/";
  };

  // Dropdown component for parent nav items with children
  const NavDropdown = ({ item }) => {
    const [dropOpen, setDropOpen] = useState(false);
    const active = item.children.some((c) => isActive(c.to));
    return (
      <div
        className="relative"
        onMouseEnter={() => setDropOpen(true)}
        onMouseLeave={() => setDropOpen(false)}
      >
        <Link
          to={item.to}
          data-testid={`nav-link-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
          className={`relative whitespace-nowrap text-base font-medium transition-colors flex items-center gap-1 ${
            active ? "text-navy-900" : "text-navy-700 hover:text-navy-900"
          }`}
        >
          {item.label}
          <ChevronDown size={12} />
          <span className={`absolute -bottom-1 left-0 h-px bg-gold-500 transition-all duration-300 ${active ? "w-full" : "w-0"}`} />
        </Link>
        <AnimatePresence>
          {dropOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 top-full pt-3 w-72"
            >
              <div className="glass rounded-2xl p-2 shadow-xl">
                {item.children.map((c) => (
                  <Link
                    key={c.label}
                    to={c.to}
                    data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s|&|\?/g, "-")}`}
                    className="block px-4 py-2.5 text-sm text-navy-700 hover:bg-gold-500/10 hover:text-navy-900 rounded-xl transition-colors"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0 mr-4">
            <img
              src={LOGO_URL}
              alt="Probate Solution OC"
              className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-heading text-lg xl:text-xl font-semibold tracking-tight text-navy-800 whitespace-nowrap">
                Probate Solution OC
              </span>
              <span className="text-[10px] font-medium tracking-[0.22em] uppercase text-gold-600 whitespace-nowrap">Orange County Real Estate</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-6">
            {NAV_LINKS.map((l) =>
              l.children ? (
                <NavDropdown key={l.label} item={l} />
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative whitespace-nowrap text-base font-medium transition-colors group ${
                    isActive(l.to) ? "text-navy-900" : "text-navy-700 hover:text-navy-900"
                  }`}
                >
                  {l.label}
                  <span className={`absolute -bottom-1 left-0 h-px bg-gold-500 transition-all duration-300 ${
                    isActive(l.to) ? "w-full" : "w-0 group-hover:w-full"
                  }`} />
                </Link>
              )
            )}
            <div
              className="relative"
              onMouseEnter={() => setMoreOpen(true)}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                data-testid="nav-more-btn"
                className="flex items-center gap-1 whitespace-nowrap text-base font-medium text-navy-700 hover:text-navy-900"
              >
                More <ChevronDown size={14} />
              </button>
              <AnimatePresence>
                {moreOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full pt-3 w-56"
                  >
                    <div className="glass rounded-2xl p-2 shadow-xl">
                      {MORE_LINKS.map((l) => (
                        <a
                          key={l.label}
                          href={l.to}
                          data-testid={`nav-more-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                          className="block px-4 py-2.5 text-sm text-navy-700 hover:bg-gold-500/10 hover:text-navy-900 rounded-xl transition-colors"
                        >
                          {l.label}
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Phone CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+17143913645"
              data-testid="nav-phone-cta"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-navy-800 text-white px-5 py-2.5 text-base font-medium hover:bg-navy-700 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Phone size={15} className="text-gold-400" />
              (714) 391-3645
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="xl:hidden p-2 rounded-full text-navy-800"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile drawer */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="xl:hidden overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="mt-4 glass rounded-2xl p-4 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <div key={l.label}>
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                      className={`block px-4 py-2.5 text-sm font-medium rounded-xl ${
                        isActive(l.to) ? "bg-gold-500/15 text-navy-900" : "text-navy-700 hover:bg-gold-500/10"
                      }`}
                    >
                      {l.label}
                    </Link>
                    {l.children && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-navy-200 pl-3">
                        {l.children.map((c) => (
                          <Link
                            key={c.label}
                            to={c.to}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-nav-sub-${c.label.toLowerCase().replace(/\s|&|\?/g, "-")}`}
                            className="block px-3 py-2 text-xs text-navy-600 hover:text-gold-600"
                          >
                            → {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.25em] text-navy-500 font-semibold">More</div>
                {MORE_LINKS.map((l) => (
                  <a
                    key={l.label}
                    href={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="px-4 py-2.5 text-sm font-medium text-navy-700 hover:bg-gold-500/10 rounded-xl"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="tel:+17143913645"
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-navy-800 text-white px-5 py-3 text-sm font-medium"
                  data-testid="mobile-phone-cta"
                >
                  <Phone size={15} className="text-gold-400" />
                  (714) 391-3645
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
